import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ShieldOff, ShieldCheck, Bot, TrendingUp } from "lucide-react";
import { Button } from "../../components/ui/button";

const AdminAIUsage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingUser, setTogglingUser] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/ai-usage`, { withCredentials: true });
        setData(r.data);
      } catch {
        toast.error("Failed to load AI usage data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggleBlock = async (userId, currentBlocked) => {
    setTogglingUser(userId);
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/block`, {}, { withCredentials: true });
      toast.success(r.data.message);
      setData((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.user_id === userId ? { ...u, blocked: r.data.blocked } : u
        ),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle block");
    } finally {
      setTogglingUser(null);
    }
  };

  const getUsageLevel = (daily, limit) => {
    const pct = (daily / limit) * 100;
    if (pct >= 100) return { color: "text-red-400", bg: "bg-red-500", barBg: "bg-red-500/20", label: "Exceeded" };
    if (pct >= 80) return { color: "text-orange-400", bg: "bg-orange-500", barBg: "bg-orange-500/20", label: "High" };
    if (pct >= 50) return { color: "text-amber-400", bg: "bg-amber-500", barBg: "bg-amber-500/20", label: "Moderate" };
    return { color: "text-emerald-400", bg: "bg-emerald-500", barBg: "bg-emerald-500/20", label: "Normal" };
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-violet-400 animate-spin" /></div>;
  if (!data) return null;

  const dailyLimit = data.daily_limit || 50;
  const activeUsers = data.users.filter((u) => u.total > 0);
  const blockedCount = data.users.filter((u) => u.blocked).length;
  const highUsageCount = data.users.filter((u) => u.daily >= dailyLimit * 0.8).length;

  return (
    <div className="space-y-6" data-testid="admin-ai-usage-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-4">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><Bot className="w-3.5 h-3.5" /> AI Users</div>
          <p className="text-2xl font-bold text-white">{activeUsers.length}</p>
        </div>
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-4">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><TrendingUp className="w-3.5 h-3.5" /> Daily Limit</div>
          <p className="text-2xl font-bold text-white">{dailyLimit}<span className="text-sm text-slate-500">/user</span></p>
        </div>
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-4">
          <div className="flex items-center gap-2 text-orange-400 text-xs mb-1"><ShieldAlert className="w-3.5 h-3.5" /> High Usage</div>
          <p className="text-2xl font-bold text-orange-400">{highUsageCount}</p>
        </div>
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-4">
          <div className="flex items-center gap-2 text-red-400 text-xs mb-1"><ShieldOff className="w-3.5 h-3.5" /> Blocked</div>
          <p className="text-2xl font-bold text-red-400">{blockedCount}</p>
        </div>
      </div>

      {/* User Usage Table */}
      <div className="bg-slate-900/70 rounded-xl border border-white/5 overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">AI Usage by User</h3>
          <button onClick={fetchUsage} className="text-xs text-slate-400 hover:text-white transition-colors">Refresh</button>
        </div>
        {activeUsers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No AI usage recorded yet</div>
        ) : (
          <div className="divide-y divide-white/5">
            {activeUsers.map((user) => {
              const level = getUsageLevel(user.daily, dailyLimit);
              const dailyPct = Math.min((user.daily / dailyLimit) * 100, 100);
              return (
                <div key={user.user_id} className="p-4 hover:bg-white/[0.02] transition-colors" data-testid={`ai-usage-row-${user.user_id}`}>
                  <div className="flex items-center gap-4">
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white truncate">{user.name || user.email}</p>
                        {user.blocked && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500/20 text-red-400" data-testid={`blocked-badge-${user.user_id}`}>BLOCKED</span>
                        )}
                        {!user.blocked && user.daily >= dailyLimit * 0.8 && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${level.barBg} ${level.color}`}>{level.label}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>

                    {/* Usage Stats */}
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-xs text-slate-500">Today</p>
                        <p className={`text-sm font-bold ${level.color}`}>{user.daily}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">7 days</p>
                        <p className="text-sm font-bold text-white">{user.weekly}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Total</p>
                        <p className="text-sm font-bold text-white">{user.total}</p>
                      </div>
                    </div>

                    {/* Daily Usage Bar */}
                    <div className="w-24 hidden lg:block">
                      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${level.bg}`} style={{ width: `${dailyPct}%` }} />
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5 text-center">{Math.round(dailyPct)}%</p>
                    </div>

                    {/* Block/Unblock Button */}
                    <Button
                      size="sm"
                      variant={user.blocked ? "outline" : "destructive"}
                      className={user.blocked
                        ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-8"
                        : "bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/30 text-xs h-8"
                      }
                      onClick={() => handleToggleBlock(user.user_id, user.blocked)}
                      disabled={togglingUser === user.user_id}
                      data-testid={`block-btn-${user.user_id}`}
                    >
                      {togglingUser === user.user_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : user.blocked ? (
                        <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Unblock</>
                      ) : (
                        <><ShieldOff className="w-3.5 h-3.5 mr-1" /> Block</>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAIUsage;
